@if (studioService.isLoading() && !studioService.isLoaded()) {
  <section class="component-library__loading py-5 d-flex justify-content-center">
    <ion-spinner name="crescent"></ion-spinner>
  </section>
} @else if (studioService.loadError()) {
  <app-api-load-error
    title="Component library unavailable"
    message="We couldn't load the component library right now. Please try again."
    retryLabel="Retry"
    (retry)="loadData(true)"
  ></app-api-load-error>
} @else {
<div class="component-library container-fluid px-0 py-3">
  <section class="component-library__hero p-4">
    <div class="d-flex flex-column flex-xl-row align-items-xl-start justify-content-between gap-4">
      <div class="component-library__hero-copy">
        <h2 class="component-library__title mb-0 bw-title-with-info">
          Component library
          <app-info-tooltip compact placement="right" text="Browse built-in HTML and CSS building blocks, then create custom reusable definitions for buttons, wrappers, sections, surfaces, and other UI blocks."></app-info-tooltip>
        </h2>
      </div>

      <div class="component-library__hero-actions d-flex flex-wrap gap-2">
        <button type="button" class="component-library__action-button" (click)="resetFilters()">
          <ion-icon name="funnel-outline"></ion-icon>
          <span>Reset filters</span>
        </button>
        <button type="button" class="component-library__action-button" (click)="createComponent()">
          <ion-icon name="add-outline"></ion-icon>
          <span>New component</span>
        </button>
      </div>
    </div>
  </section>

  <div class="component-library__stats mt-3">
    <article class="component-library__stat-card p-3">
      <span class="component-library__stat-label">Total</span>
      <strong>{{ studioService.stats().total }}</strong>
    </article>
    <article class="component-library__stat-card p-3">
      <span class="component-library__stat-label">Built-in</span>
      <strong>{{ studioService.stats().builtIn }}</strong>
    </article>
    <article class="component-library__stat-card p-3">
      <span class="component-library__stat-label">Custom</span>
      <strong>{{ studioService.stats().custom }}</strong>
    </article>
    <article class="component-library__stat-card p-3">
      <span class="component-library__stat-label">Ready</span>
      <strong>{{ studioService.stats().ready }}</strong>
    </article>
    <article class="component-library__stat-card p-3">
      <span class="component-library__stat-label">Usage</span>
      <strong>{{ studioService.stats().totalUsage }}</strong>
    </article>
  </div>

  <section class="component-library__filters mt-3 p-4">
    <div class="component-library__filter-grid">
      <label class="component-library__field component-library__field--wide">
        <span class="component-library__field-label">Search</span>
        <div class="component-library__search-wrap">
          <ion-icon name="search-outline"></ion-icon>
          <input class="component-library__input" type="text" [(ngModel)]="searchTerm" placeholder="Search by name, description, or tags" />
        </div>
      </label>

      <label class="component-library__field">
        <span class="component-library__field-label">Source</span>
        <select class="component-library__select" [(ngModel)]="sourceFilter">
          <option value="all">Built-in and custom</option>
          <option value="built-in">Built-in only</option>
          <option value="custom">Custom only</option>
        </select>
      </label>

      <label class="component-library__field">
        <span class="component-library__field-label">Category</span>
        <select class="component-library__select" [(ngModel)]="categoryFilter">
          <option value="all">All categories</option>
          @for (category of categories; track category) {
            <option [value]="category">{{ category | titlecase }}</option>
          }
        </select>
      </label>

      <label class="component-library__field">
        <span class="component-library__field-label">Status</span>
        <select class="component-library__select" [(ngModel)]="statusFilter">
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="ready">Ready</option>
        </select>
      </label>

      <label class="component-library__field">
        <span class="component-library__field-label">Sort</span>
        <select class="component-library__select" [(ngModel)]="sortBy">
          <option value="updated-desc">Updated: newest first</option>
          <option value="updated-asc">Updated: oldest first</option>
          <option value="name-asc">Name: A-Z</option>
          <option value="name-desc">Name: Z-A</option>
          <option value="usage-desc">Usage: highest first</option>
          <option value="usage-asc">Usage: lowest first</option>
        </select>
      </label>
    </div>
  </section>

  <div class="component-library__grid mt-3">
    @for (definition of filteredDefinitions; track definition.id) {
      <article class="component-library__card p-4" (contextmenu)="openDefinitionContextMenu($event, definition)">
        <div class="d-flex align-items-start justify-content-between gap-3">
          <div>
            <div class="component-library__card-topline mb-1">
              <p class="component-library__card-category mb-0">{{ definition.category | titlecase }}</p>
              <span class="component-library__source-chip" [class.is-custom]="definition.source === 'custom'">
                {{ sourceLabel(definition) }}
              </span>
            </div>
            <h3 class="component-library__card-title mb-2">{{ definition.name }}</h3>
            <p class="component-library__card-description mb-0">{{ definition.description }}</p>
          </div>
          <span class="component-library__status-chip" [class.is-ready]="definition.status === 'ready'">
            {{ definition.status | titlecase }}
          </span>
        </div>

        <div class="component-library__preview mt-3">
          <div class="component-library__preview-shell">
            @for (label of previewLabels(definition); track label) {
              <div class="component-library__preview-block">{{ label }}</div>
            }
          </div>
        </div>

        <div class="component-library__tags mt-3">
          @for (tag of definition.tags; track tag) {
            <span class="component-library__tag">{{ tag }}</span>
          }
        </div>

        <div class="component-library__meta mt-3">
          <span>Version {{ definition.version }}</span>
          <span>{{ definition.usageCount }} uses</span>
          <span>{{ definition.updatedAt | date: 'MMM d, y' }}</span>
        </div>

        <div class="component-library__actions mt-3">
          @if (definition.source === 'built-in') {
            <button type="button" class="component-library__card-button is-primary" (click)="duplicateDefinition(definition.id)">
              <ion-icon name="copy-outline"></ion-icon>
              <span>Duplicate</span>
            </button>
          } @else {
            <button
              type="button"
              class="component-library__card-button"
              (click)="deleteDefinition(definition.id)"
            >
              <ion-icon name="trash-outline"></ion-icon>
              <span>Delete</span>
            </button>
            <button type="button" class="component-library__card-button" (click)="duplicateDefinition(definition.id)">
              <ion-icon name="copy-outline"></ion-icon>
              <span>Duplicate</span>
            </button>
            <button type="button" class="component-library__card-button is-primary" (click)="openInBuilder(definition.id)">
              <ion-icon name="construct-outline"></ion-icon>
              <span>Edit</span>
            </button>
          }
        </div>
      </article>
    } @empty {
      <section class="component-library__empty p-4">
        <h3 class="component-library__title mb-0 bw-title-with-info">
          No components match the current filters
          <app-info-tooltip compact placement="right" text="Clear filters or create a new component to continue."></app-info-tooltip>
        </h3>
      </section>
    }
  </div>
</div>
}
